export const certificateFontFamilies = [
  "DejaVu Sans",
  "DejaVu Serif",
  "DejaVu Sans Mono",
];
export const certificateFieldKeys = [
  "recipient_name",
  "recipient_ic",
  "certificate_no",
  "issued_at",
];
export const certificateFieldLabels = {
  recipient_name: "label.certificateRecipientName",
  recipient_ic: "label.certificateRecipientIc",
  certificate_no: "label.certificateNumber",
  issued_at: "label.certificateIssueDate",
};
const enabled = (value) => [true, 1, "1"].includes(value);
const legacyFonts = {
  Helvetica: "DejaVu Sans",
  "Times-Roman": "DejaVu Serif",
  Courier: "DejaVu Sans Mono",
  "Zapf-Dingbats": "DejaVu Sans",
};
export const certificatePreviewFont = (family) =>
  ({
    "DejaVu Sans": '"DejaVu Sans", Arial, sans-serif',
    "DejaVu Serif": '"DejaVu Serif", "Times New Roman", serif',
    "DejaVu Sans Mono": '"DejaVu Sans Mono", Courier, monospace',
  }[family] || "Arial, sans-serif");
export const certificateDefaultLayout = () => ({
  recipient_name: {
    enabled: true,
    x: 12,
    y: 42,
    width: 76,
    font_size: 34,
    font_family: "DejaVu Serif",
    color: "#142E3A",
    align: "center",
  },
  recipient_ic: {
    enabled: true,
    x: 12,
    y: 56,
    width: 76,
    font_size: 16,
    font_family: "DejaVu Sans Mono",
    color: "#656E6F",
    align: "center",
  },
  certificate_no: {
    enabled: false,
    x: 10,
    y: 85,
    width: 40,
    font_size: 12,
    font_family: "DejaVu Sans",
    color: "#000000",
    align: "left",
  },
  issued_at: {
    enabled: false,
    x: 60,
    y: 85,
    width: 30,
    font_size: 12,
    font_family: "DejaVu Sans",
    color: "#000000",
    align: "right",
  },
});
export function certificateLayout(value) {
  const parsed = typeof value === "string" ? JSON.parse(value) : value;
  if (parsed == null) return certificateDefaultLayout();
  if (typeof parsed !== "object" || Array.isArray(parsed))
    throw new Error("Invalid certificate layout.");
  const defaults = certificateDefaultLayout();
  const layout = { ...parsed };
  certificateFieldKeys.forEach((key) => {
    const config = parsed[key];
    if (config != null && (typeof config !== "object" || Array.isArray(config)))
      throw new Error("Invalid certificate field.");
    layout[key] = { ...defaults[key], ...config };
    layout[key].enabled =
      key === "recipient_name" ||
      (config?.enabled == null
        ? key === "recipient_ic" && config != null
        : enabled(config.enabled));
    layout[key].font_family =
      legacyFonts[layout[key].font_family] || layout[key].font_family;
  });
  return layout;
}
export function certificateLayoutPayload(layout) {
  return Object.fromEntries(
    certificateFieldKeys.map((key) => [
      key,
      key !== "recipient_name" && !layout[key]?.enabled
        ? { enabled: false }
        : { ...layout[key], enabled: true },
    ]),
  );
}
export const certificateModel = (data = {}) => ({
  uuid: data.uuid,
  name: data.name || "",
  templatePath: data.template_path || "",
  layoutConfig: certificateLayout(data.layout_config),
  isActive: enabled(data.is_active),
  isCompleted: enabled(data.is_completed),
  isClaimed: enabled(data.is_claimed),
  isPdfReady: enabled(data.is_pdf_ready),
  issuedAt: data.issued_at || null,
});
export function validCertificateLayout(layout) {
  return (
    layout?.recipient_name?.enabled === true &&
    certificateFieldKeys.every((key) => {
      const item = layout?.[key];
      if (key !== "recipient_name" && item?.enabled === false) return true;
      return (
        item?.enabled === true &&
        ["x", "y", "width", "font_size"].every(
          (field) =>
            typeof item[field] === "number" && Number.isFinite(item[field]),
        ) &&
        item.x >= 0 &&
        item.x <= 100 &&
        item.y >= 0 &&
        item.y <= 100 &&
        item.width > 0 &&
        item.width <= 100 &&
        item.x + item.width <= 100 &&
        item.font_size > 0 &&
        certificateFontFamilies.includes(item.font_family) &&
        /^#[0-9a-f]{6}$/i.test(item.color) &&
        ["left", "center", "right"].includes(item.align)
      );
    })
  );
}
export const certificatePreviewFields = (layout) =>
  certificateFieldKeys
    .filter(
      (key) => key === "recipient_name" || layout?.[key]?.enabled === true,
    )
    .map((key) => ({
      key,
      label: certificateFieldLabels[key],
      generated: ["certificate_no", "issued_at"].includes(key),
    }));
export function certificateRecipientPayload(layout, recipient) {
  const payload = {};
  certificatePreviewFields(layout)
    .filter((field) => !field.generated)
    .forEach((field) => {
      const value = recipient[field.key]?.trim();
      if (!value) throw new Error("Recipient details required.");
      payload[field.key] = value;
    });
  return payload;
}
export const certificateClaimAction = (certificate) =>
  certificate.isClaimed
    ? certificate.isPdfReady
      ? "download"
      : "retry"
    : certificate.isCompleted
    ? "claim"
    : "incomplete";
